import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Pencil, Plus, Search, ShieldAlert, UserPlus, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import { useFormik } from 'formik';
import { PERMISSION_GROUPS, PERMISSION_LABELS } from '../../constants/permissions';
import { useAuth } from '../../context/AuthContext';
import Dialog from '../../components/Dialog';

const emptyAdminForm = (user, institutionScope) => ({
  name: '',
  email: '',
  role: institutionScope ? 'institution_admin' : 'cluster_admin',
  managedInstitutionId: user?.managedInstitutionId || '',
  managedClusterId: user?.managedClusterId || '',
  isActive: true,
  permissions: [],
});

const PAGE_PERMISSION_DEPENDENCIES = {
  dashboard: [
    'stats.revenue',
    'stats.admissions',
    'stats.students',
    'stats.payments',
    'dashboard.revenue_analytics',
    'dashboard.admission_funnel',
    'dashboard.college_analytics',
    'dashboard.course_analytics',
    'dashboard.admission_intelligence',
    'dashboard.geographic_origin',
    'dashboard.peak_timings',
    'dashboard.device_usage',
    'dashboard.activity_feed',
  ],
  admissions: ['admissions.edit', 'admissions.export', 'institutions.view'],
  institutions: ['institutions.create', 'institutions.edit', 'linked_accounts.view', 'linked_accounts.edit'],
  linked_accounts: ['linked_accounts.edit'],
  courses: ['courses.create', 'courses.edit', 'courses.delete'],
  settlements: ['settlements.refresh'],
  settings: ['settings.edit'],
  user_management: ['admins.manage'],
};

const getPermissionNamespace = (permission = '') => String(permission).split('.')[0];
const getPermissionDependencies = (permission) => PAGE_PERMISSION_DEPENDENCIES[getPermissionNamespace(permission)] || [];
const isPagePermission = (permission = '') => permission.endsWith('.view');
const permissionLabel = (permission) => PERMISSION_LABELS[permission] || permission;
const SUPER_ADMIN_ONLY_PERMISSIONS = new Set(['institutions.create']);

export default function AdminUserManagement({ apiBase = '/api/admin', institutionScope = false, editMode = false }) {
  const { userId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const routeBase = institutionScope
    ? '/institution'
    : (user?.role === 'cluster_admin' ? '/cluster-admin' : '/admin');

  useEffect(() => {
    if (editMode) return;
    if (user?.role === 'cluster_admin') {
      setAdminForm((prev) => ({
        ...prev,
        role: 'cluster_admin',
        managedClusterId: user?.managedClusterId || prev.managedClusterId,
      }));
    }
  }, [user, editMode]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [clusters, setClusters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adminSaving, setAdminSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [adminForm, setAdminForm] = useState(emptyAdminForm(user, institutionScope));
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createStep, setCreateStep] = useState('details');
  const [existingAdmin, setExistingAdmin] = useState(null);
  const [roleGroups, setRoleGroups] = useState([]);
  const [newRoleGroupName, setNewRoleGroupName] = useState('');
  const [savingRoleGroup, setSavingRoleGroup] = useState(false);
  const [selectedEditRoleGroupId, setSelectedEditRoleGroupId] = useState('');
  const [selectedCreateRoleGroupId, setSelectedCreateRoleGroupId] = useState('');
  const [adminErrors, setAdminErrors] = useState({});

  const decodedUserId = userId ? decodeURIComponent(userId) : '';
  const canAssignPermission = (permission) => user?.role === 'admin' || !SUPER_ADMIN_ONLY_PERMISSIONS.has(permission);
  const visiblePermissionGroups = useMemo(() => PERMISSION_GROUPS
    .map((group) => ({ ...group, permissions: group.permissions.filter(canAssignPermission) }))
    .filter((group) => group.permissions.length), [user?.role]);


  useEffect(() => {
    fetchAdminUsers();
    fetchRoleGroups();
    if (!institutionScope) {
      fetchInstitutions();
      fetchClusters();
    }
  }, [apiBase, institutionScope]);

  const fetchRoleGroups = async () => {
    try {
      const res = await axios.get(`${apiBase}/role-groups`);
      setRoleGroups(Array.isArray(res.data) ? res.data : []);
    } catch {
      setRoleGroups([]);
    }
  };

  const applyRoleGroup = (groupId, target = 'edit') => {
    const group = roleGroups.find((item) => item.id === groupId);
    if (!group) return;
    const permissions = Array.isArray(group.permissions) ? group.permissions.filter(canAssignPermission) : [];
    if (target === 'create') {
      setSelectedCreateRoleGroupId(groupId);
      createFormik.setFieldValue('permissions', permissions);
    } else {
      setSelectedEditRoleGroupId(groupId);
      setAdminForm((prev) => ({ ...prev, permissions }));
    }
    toast.success(`${group.name} permissions selected`);
  };

  const createRoleGroup = async (permissions) => {
    const name = newRoleGroupName.trim();
    if (!name) return toast.error('Enter a role group name');
    if (!permissions.length) return toast.error('Select permissions before saving the role group');
    setSavingRoleGroup(true);
    try {
      const res = await axios.post(`${apiBase}/role-groups`, { name, permissions });
      setRoleGroups((prev) => [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name)));
      setNewRoleGroupName('');
      toast.success('Custom role group created');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create role group');
    } finally {
      setSavingRoleGroup(false);
    }
  };

  const visibleAdminUsers = useMemo(() => {
    const admins = institutionScope
      ? adminUsers.filter((admin) => admin.role === 'institution_admin')
      : adminUsers.filter((admin) => admin.role === 'institution_admin' || admin.role === 'cluster_admin');
    const search = searchTerm.trim().toLowerCase();
    if (!search) return admins;
    return admins.filter((admin) => [
      admin.name,
      admin.email,
      admin.role,
    ].filter(Boolean).join(' ').toLowerCase().includes(search));
  }, [adminUsers, institutionScope, searchTerm]);

  const selectedAdmin = useMemo(() => (
    adminUsers.find((admin) => String(admin.id || admin._id) === String(decodedUserId))
  ), [adminUsers, decodedUserId]);

  useEffect(() => {
    if (!editMode || loading) return;
    if (!selectedAdmin) {
      toast.error('Admin user not found');
      navigate(`${routeBase}/user-management`, { replace: true });
      return;
    }
    setAdminForm({
      name: selectedAdmin.name || '',
      email: selectedAdmin.email || '',
      role: selectedAdmin.role || 'admin',
      managedInstitutionId: selectedAdmin.managedInstitutionId || user?.managedInstitutionId || '',
      managedClusterId: selectedAdmin.managedClusterId || user?.managedClusterId || '',
      isActive: selectedAdmin.isActive !== false,
      permissions: Array.isArray(selectedAdmin.permissions) ? selectedAdmin.permissions : [],
    });
  }, [editMode, loading, navigate, routeBase, selectedAdmin, user?.managedInstitutionId, user?.managedClusterId]);

  const fetchAdminUsers = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${apiBase}/admin-users`);
      setAdminUsers(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load admin users');
    } finally {
      setLoading(false);
    }
  };

  const fetchInstitutions = async () => {
    try {
      const res = await axios.get('/api/admin/institutions?export=true');
      setInstitutions(res.data?.items || res.data || []);
    } catch {
      setInstitutions([]);
    }
  };

  const fetchClusters = async () => {
    try {
      const res = await axios.get('/api/admin/clusters');
      setClusters(res.data || []);
    } catch {
      setClusters([]);
    }
  };

  const togglePermission = (permission) => {
    setAdminErrors((prev) => ({ ...prev, permissions: '' }));
    setAdminForm((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter((item) => (
          item !== permission && (!isPagePermission(permission) || !getPermissionDependencies(permission).includes(item))
        ))
        : [...new Set([...prev.permissions, permission, ...(isPagePermission(permission) ? getPermissionDependencies(permission) : [])])],
    }));
  };

  const handleRoleChange = (role) => {
    setAdminErrors({});
    setSelectedEditRoleGroupId('');
    setAdminForm((prev) => ({
      ...prev,
      role,
      managedInstitutionId: role === 'institution_admin' ? prev.managedInstitutionId : '',
      managedClusterId: role === 'cluster_admin' ? (user?.role === 'cluster_admin' ? user?.managedClusterId || '' : prev.managedClusterId) : '',
      permissions: [],
    }));
  };

  const applyRoleDefaults = (values, role) => ({
    ...values,
    role,
	    managedInstitutionId: role === 'institution_admin' ? values.managedInstitutionId : '',
	    managedClusterId: role === 'cluster_admin'
	      ? (user?.role === 'cluster_admin' ? user?.managedClusterId || '' : values.managedClusterId)
	      : '',
	    permissions: [],
	  });

  const createFormik = useFormik({
    initialValues: emptyAdminForm(user, institutionScope),
    enableReinitialize: true,
    validate: (values) => {
      const errors = {};
      if (!values.email.trim()) errors.email = 'Admin email is required';
      if (!institutionScope && values.role === 'institution_admin' && !values.managedInstitutionId) errors.managedInstitutionId = 'Select an institution';
	      if (!institutionScope && values.role === 'cluster_admin' && !values.managedClusterId) errors.managedClusterId = 'Select a cluster';
      if (createStep === 'access' && values.permissions.length === 0) errors.permissions = 'Select at least one access permission';
      return errors;
    },
    onSubmit: async (values) => {
      if (createStep === 'details') {
        const existing = adminUsers.find((admin) => String(admin.email || '').trim().toLowerCase() === values.email.trim().toLowerCase());
        if (existing) {
          setExistingAdmin(existing);
          setCreateStep('existing');
          return;
        }
        setCreateStep('access');
        return;
      }

      setAdminSaving(true);
      try {
	        const payload = {
	          ...values,
	          role: institutionScope ? 'institution_admin' : values.role,
	          managedInstitutionId: institutionScope ? user?.managedInstitutionId : values.managedInstitutionId,
	          managedClusterId: values.role === 'cluster_admin' ? values.managedClusterId : null,
	        };
        if (existingAdmin) {
          await axios.patch(`${apiBase}/admin-users/${existingAdmin.id || existingAdmin._id}`, payload);
          toast.success('Admin role and access updated');
        } else {
          await axios.post(`${apiBase}/admin-users`, payload);
          toast.success('Admin user created');
        }
        setShowCreateDialog(false);
        setCreateStep('details');
        setExistingAdmin(null);
        createFormik.resetForm();
        fetchAdminUsers();
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to save admin user');
      } finally {
        setAdminSaving(false);
      }
    },
  });

  const toggleCreatePermission = (permission) => {
    createFormik.setFieldValue('permissions', createFormik.values.permissions.includes(permission)
      ? createFormik.values.permissions.filter((item) => (
        item !== permission && (!isPagePermission(permission) || !getPermissionDependencies(permission).includes(item))
      ))
      : [...new Set([...createFormik.values.permissions, permission, ...(isPagePermission(permission) ? getPermissionDependencies(permission) : [])])]);
  };

  const openCreateDialog = () => {
    setCreateStep('details');
    setExistingAdmin(null);
    setSelectedCreateRoleGroupId('');
    createFormik.resetForm({ values: emptyAdminForm(user, institutionScope) });
    setShowCreateDialog(true);
  };

  const closeCreateDialog = () => {
    if (adminSaving) return;
    setShowCreateDialog(false);
    setCreateStep('details');
    setExistingAdmin(null);
    setSelectedCreateRoleGroupId('');
    createFormik.resetForm();
  };

  const saveAdminUser = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!adminForm.email.trim()) errors.email = 'Admin email is required';
    if (!institutionScope && adminForm.role === 'institution_admin' && !adminForm.managedInstitutionId) errors.managedInstitutionId = 'Select an institution';
    if (!institutionScope && adminForm.role === 'cluster_admin' && !adminForm.managedClusterId) errors.managedClusterId = 'Select a cluster';
    if (!adminForm.permissions.length) errors.permissions = 'Select a role group or at least one permission';
    setAdminErrors(errors);
    if (Object.keys(errors).length) return;
    setAdminSaving(true);
    try {
      const payload = {
        ...adminForm,
        role: institutionScope ? 'institution_admin' : adminForm.role,
        managedInstitutionId: institutionScope ? user?.managedInstitutionId : adminForm.managedInstitutionId,
	        managedClusterId: adminForm.role === 'cluster_admin' ? adminForm.managedClusterId : null,
      };
      const url = editMode ? `${apiBase}/admin-users/${decodedUserId}` : `${apiBase}/admin-users`;
      const method = editMode ? 'patch' : 'post';
      await axios[method](url, payload);
      toast.success(editMode ? 'Admin permissions updated' : 'Admin user created');
      navigate(`${routeBase}/user-management`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save admin user');
    } finally {
      setAdminSaving(false);
    }
  };

  if (loading) return <div className="h-64 flex items-center justify-center"><div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div></div>;

  if (editMode) {
    return (
      <div>
        <div className="mb-8">
          <Link to={`${routeBase}/user-management`} className="inline-flex items-center gap-2 text-indigo-600 font-bold mb-4 hover:text-indigo-700">
            <ArrowLeft size={16} /> Back to User Management
          </Link>
          <h1 className="text-3xl font-extrabold text-slate-800" style={{ fontFamily: 'Poppins' }}>Manage Permissions</h1>
          <p className="text-slate-500 mt-1">{selectedAdmin?.name || selectedAdmin?.email || selectedAdmin?.mobile}</p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden max-w-5xl">
          <div className="bg-slate-50 p-6 border-b border-slate-200 flex items-center gap-4">
            <div className="w-12 h-12 bg-indigo-100 text-indigo-600 rounded-xl flex items-center justify-center">
              <ShieldAlert size={24} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-800">Admin Details</h2>
              <p className="text-sm text-slate-500">Edit role, status, institution scope and permission access</p>
            </div>
          </div>

          <form onSubmit={saveAdminUser} className="p-6 space-y-5 w-">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="block">
                <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-400">Name</span>
                <input value={adminForm.name} onChange={(e) => setAdminForm(prev => ({ ...prev, name: e.target.value }))} placeholder="Name" className="w-full px-4 py-3 border border-slate-200 rounded-xl outline-none" />
              </label>
              <label className="block">
                <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-400">Email *</span>
                <input value={adminForm.email} onChange={(e) => { setAdminForm(prev => ({ ...prev, email: e.target.value })); setAdminErrors(prev => ({ ...prev, email: '' })); }} placeholder="Email" className="w-full px-4 py-3 border border-slate-200 rounded-xl outline-none" />
                {adminErrors.email && <p className="mt-1 text-xs font-semibold text-rose-600">{adminErrors.email}</p>}
              </label>
              {!institutionScope && (
                <label className="block">
                  <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-400">Role *</span>
                  <select value={adminForm.role} onChange={(e) => handleRoleChange(e.target.value)} className="w-full px-4 py-3 border border-slate-200 rounded-xl outline-none bg-white">
                    <option value="cluster_admin">Cluster Admin</option>
                    <option value="institution_admin">Institution Admin</option>
                  </select>
                </label>
              )}
              {!institutionScope && adminForm.role === 'institution_admin' && (
                <label className="block">
                  <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-400">Institution *</span>
                  <select value={adminForm.managedInstitutionId} onChange={(e) => { setAdminForm(prev => ({ ...prev, managedInstitutionId: e.target.value })); setAdminErrors(prev => ({ ...prev, managedInstitutionId: '' })); }} className="w-full px-4 py-3 border border-slate-200 rounded-xl outline-none bg-white">
                    <option value="">Select institution</option>
                    {institutions.map((institution) => <option key={institution.id || institution._id} value={institution.id || institution._id}>{institution.displayName || institution.collegeName}</option>)}
                  </select>
                  {adminErrors.managedInstitutionId && <p className="mt-1 text-xs font-semibold text-rose-600">{adminErrors.managedInstitutionId}</p>}
                </label>
              )}
              {!institutionScope && adminForm.role === 'cluster_admin' && user?.role !== 'cluster_admin' && (
                <label className="block">
                  <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-400">Cluster *</span>
                  <select value={adminForm.managedClusterId} onChange={(e) => { setAdminForm(prev => ({ ...prev, managedClusterId: e.target.value })); setAdminErrors(prev => ({ ...prev, managedClusterId: '' })); }} className="w-full px-4 py-3 border border-slate-200 rounded-xl outline-none bg-white">
                    <option value="">Select cluster</option>
                    {clusters.map((cluster) => <option key={cluster.id || cluster._id} value={cluster.id || cluster._id}>{cluster.name}</option>)}
                  </select>
                  {adminErrors.managedClusterId && <p className="mt-1 text-xs font-semibold text-rose-600">{adminErrors.managedClusterId}</p>}
                </label>
              )}
            </div>
            {!institutionScope && adminForm.role === 'cluster_admin' && user?.role === 'cluster_admin' && (
              <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                This Cluster Admin will be assigned automatically to your cluster. Cluster Admins cannot create users outside their own cluster.
              </p>
            )}

            <label className="flex items-center gap-2 text-sm font-bold text-slate-700">
              <input type="checkbox" checked={adminForm.isActive} onChange={(e) => setAdminForm(prev => ({ ...prev, isActive: e.target.checked }))} className="h-4 w-4 accent-indigo-600" />
              Active
            </label>

            <div className="rounded-xl border border-slate-200 p-4 space-y-5">
              <div className="rounded-xl bg-indigo-50 p-4">
                <p className="mb-2 text-xs font-black uppercase tracking-wider text-indigo-700">Role group</p>
                <div className="grid gap-2 md:grid-cols-[1fr_1fr_auto]">
                  <select value={selectedEditRoleGroupId} onChange={(e) => { applyRoleGroup(e.target.value); setAdminErrors(prev => ({ ...prev, permissions: '' })); }} className="rounded-xl border border-indigo-200 bg-white px-3 py-2 text-sm">
                    <option value="" disabled>Select a role group</option>
                    {roleGroups.map((group) => <option key={group.id} value={group.id}>{group.name}{group.isDefault ? ' (Default)' : ''}</option>)}
                  </select>
                  <input value={newRoleGroupName} onChange={(e) => setNewRoleGroupName(e.target.value)} placeholder="New role group name" className="rounded-xl border border-indigo-200 px-3 py-2 text-sm" />
                  <button type="button" disabled={savingRoleGroup} onClick={() => createRoleGroup(adminForm.permissions)} className="inline-flex items-center justify-center gap-1 rounded-xl bg-indigo-600 px-3 py-2 text-sm font-bold text-white disabled:opacity-50">
                    <Plus size={16} /> Save group
                  </button>
                </div>
                <p className="mt-2 text-xs text-indigo-600">Selecting a group replaces the current permission selection. You can adjust it afterward.</p>
                {adminErrors.permissions && <p className="mt-2 text-xs font-semibold text-rose-600">{adminErrors.permissions}</p>}
              </div>
              {visiblePermissionGroups.map((group) => (
                <div key={group.title}>
                  <div className="mb-2 flex items-center justify-between gap-3">
                    <p className="text-xs font-black uppercase tracking-wider text-slate-400">{group.title}</p>
                    <button
                      type="button"
                      onClick={() => {
                        const expandedPermissions = group.permissions.flatMap((permission) => (
                          isPagePermission(permission) ? [permission, ...getPermissionDependencies(permission)] : [permission]
                        ));
                        const groupSet = new Set(expandedPermissions);
                        const allSelected = expandedPermissions.every((permission) => adminForm.permissions.includes(permission));
                        setAdminForm((prev) => ({
                          ...prev,
                          permissions: allSelected
                            ? prev.permissions.filter((permission) => !groupSet.has(permission))
                            : [...new Set([...prev.permissions, ...expandedPermissions])],
                        }));
                      }}
                      className="text-xs font-bold text-indigo-600 hover:text-indigo-700"
                    >
                      Toggle group
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
                    {group.permissions.map((permission) => (
                      <label key={permission} className="flex items-center gap-2 text-sm text-slate-700">
                        <input type="checkbox" checked={adminForm.permissions.includes(permission)} onChange={() => togglePermission(permission)} className="h-4 w-4 accent-indigo-600" />
                        {permissionLabel(permission)}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-3">
              <Link to={`${routeBase}/user-management`} className="px-5 py-3 border border-slate-200 text-slate-700 font-bold rounded-xl hover:bg-slate-50">
                Cancel
              </Link>
              <button type="submit" disabled={adminSaving} className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 disabled:opacity-50">
                <CheckCircle2 size={18} />
                {adminSaving ? 'Saving...' : 'Save Permissions'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-800" style={{ fontFamily: 'Poppins' }}>User Management</h1>
          <p className="text-slate-500 mt-1">Admins and institutional admins with permission access.</p>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search admins..."
            className="pl-10 pr-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none w-64 md:w-80"
          />
        </div>
      </div>

      <div className="mb-6 flex justify-end">
        <button
          type="button"
          onClick={openCreateDialog}
          className="inline-flex items-center justify-center gap-2 px-2 py-2 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700"
        >
          <UserPlus size={18} />
          Create Admin User
        </button>
      </div>

      <Dialog
        isOpen={showCreateDialog}
        onClose={closeCreateDialog}
        title={createStep === 'access' ? 'Select Access' : createStep === 'existing' ? 'Admin Already Exists' : 'Create Admin User'}
        description={createStep === 'access'
          ? 'Page access auto-selects related features. You can uncheck any feature before saving.'
          : createStep === 'existing'
            ? `${existingAdmin?.email || createFormik.values.email} is already an admin. Continue to change role and access for this admin.`
            : 'Enter admin details first. Access will be selected in the next step.'}
        icon={<ShieldAlert className="text-indigo-600" size={22} />}
        maxWidthClass="max-w-4xl"
      >
        <form onSubmit={createFormik.handleSubmit} className="space-y-5">
          {createStep === 'existing' ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
              <p className="text-sm font-bold text-amber-900">{existingAdmin?.name || 'Admin User'}</p>
              <p className="text-xs text-amber-800 mt-1">{existingAdmin?.email}</p>
              <p className="text-xs text-amber-700 mt-2">Current role: {String(existingAdmin?.role || '').replace(/_/g, ' ')}</p>
            </div>
          ) : null}

          {createStep === 'details' || createStep === 'existing' ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="block">
                  <span className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Name</span>
                  <input
                    name="name"
                    value={createFormik.values.name}
                    onChange={createFormik.handleChange}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl outline-none"
                    placeholder="Name"
                  />
                </label>
                <label className="block">
                  <span className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Email *</span>
                  <input
                    name="email"
                    type="email"
                    value={createFormik.values.email}
                    onChange={createFormik.handleChange}
                    disabled={createStep === 'existing'}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl outline-none disabled:bg-slate-100 disabled:text-slate-500"
                    placeholder="admin@example.com"
                  />
                  {createFormik.errors.email && <p className="mt-1 text-xs font-semibold text-rose-600">{createFormik.errors.email}</p>}
                </label>
                {!institutionScope && (
                  <label className="block">
                    <span className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Role</span>
                    <select
                      name="role"
                      value={createFormik.values.role}
                      onChange={(e) => {
                        setSelectedCreateRoleGroupId('');
                        createFormik.setValues(applyRoleDefaults(createFormik.values, e.target.value));
                      }}
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl outline-none bg-white"
                    >
                      <option value="cluster_admin">Cluster Admin</option>
                      <option value="institution_admin">Institution Admin</option>
                    </select>
                  </label>
                )}
	                {!institutionScope && createFormik.values.role === 'cluster_admin' && user?.role !== 'cluster_admin' && (
	                  <label className="block">
	                    <span className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Cluster *</span>
	                    <select
	                      name="managedClusterId"
	                      value={createFormik.values.managedClusterId}
	                      onChange={createFormik.handleChange}
	                      className="w-full px-4 py-3 border border-slate-200 rounded-xl outline-none bg-white"
	                    >
	                      <option value="">Select cluster</option>
	                      {clusters.map((cluster) => <option key={cluster.id || cluster._id} value={cluster.id || cluster._id}>{cluster.name}</option>)}
	                    </select>
	                    {createFormik.errors.managedClusterId && <p className="mt-1 text-xs font-semibold text-rose-600">{createFormik.errors.managedClusterId}</p>}
	                  </label>
	                )}
                {!institutionScope && createFormik.values.role === 'institution_admin' && (
                  <label className="block">
                    <span className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Institution *</span>
                    <select
                      name="managedInstitutionId"
                      value={createFormik.values.managedInstitutionId}
                      onChange={createFormik.handleChange}
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl outline-none bg-white"
                    >
                      <option value="">Select institution</option>
                      {institutions.map((institution) => (
                        <option key={institution.id || institution._id} value={institution.id || institution._id}>
                          {institution.displayName || institution.collegeName}
                        </option>
                      ))}
                    </select>
                    {createFormik.errors.managedInstitutionId && <p className="mt-1 text-xs font-semibold text-rose-600">{createFormik.errors.managedInstitutionId}</p>}
                  </label>
                )}
              </div>
              {!institutionScope && createFormik.values.role === 'cluster_admin' && user?.role === 'cluster_admin' && (
                <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                  This Cluster Admin will be assigned automatically to your cluster. You can only create Cluster Admins within your own cluster.
                </p>
              )}
              <label className="flex items-center gap-2 text-sm font-bold text-slate-700">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={createFormik.values.isActive}
                  onChange={createFormik.handleChange}
                  className="h-4 w-4 accent-indigo-600"
                />
                Active
              </label>
            </>
          ) : null}

          {createStep === 'access' ? (
            <div className="rounded-xl border border-slate-200 p-4 space-y-5 max-h-[55vh] overflow-y-auto">
              {createFormik.errors.permissions && <p className="text-xs font-semibold text-rose-600">{createFormik.errors.permissions}</p>}
              <div className="rounded-xl bg-indigo-50 p-4">
                <p className="mb-2 text-xs font-black uppercase tracking-wider text-indigo-700">Role group</p>
                <div className="grid gap-2 md:grid-cols-[1fr_1fr_auto]">
                  <select value={selectedCreateRoleGroupId} onChange={(e) => applyRoleGroup(e.target.value, 'create')} className="rounded-xl border border-indigo-200 bg-white px-3 py-2 text-sm">
                    <option value="" disabled>Select a role group</option>
                    {roleGroups.map((group) => <option key={group.id} value={group.id}>{group.name}{group.isDefault ? ' (Default)' : ''}</option>)}
                  </select>
                  <input value={newRoleGroupName} onChange={(e) => setNewRoleGroupName(e.target.value)} placeholder="New role group name" className="rounded-xl border border-indigo-200 px-3 py-2 text-sm" />
                  <button type="button" disabled={savingRoleGroup} onClick={() => createRoleGroup(createFormik.values.permissions)} className="inline-flex items-center justify-center gap-1 rounded-xl bg-indigo-600 px-3 py-2 text-sm font-bold text-white disabled:opacity-50">
                    <Plus size={16} /> Save group
                  </button>
                </div>
                <p className="mt-2 text-xs text-indigo-600">Selecting a group automatically applies its permissions. You can still fine-tune them below.</p>
              </div>
              {visiblePermissionGroups.map((group) => (
                <div key={group.title}>
                  <div className="mb-2 flex items-center justify-between gap-3">
                    <p className="text-xs font-black uppercase tracking-wider text-slate-400">{group.title}</p>
                    <button
                      type="button"
                      onClick={() => {
                        const expandedPermissions = group.permissions.flatMap((permission) => (
                          isPagePermission(permission) ? [permission, ...getPermissionDependencies(permission)] : [permission]
                        ));
                        const groupSet = new Set(expandedPermissions);
                        const allSelected = expandedPermissions.every((permission) => createFormik.values.permissions.includes(permission));
                        createFormik.setFieldValue('permissions', allSelected
                          ? createFormik.values.permissions.filter((permission) => !groupSet.has(permission))
                          : [...new Set([...createFormik.values.permissions, ...expandedPermissions])]);
                      }}
                      className="text-xs font-bold text-indigo-600 hover:text-indigo-700"
                    >
                      Toggle group
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
                    {group.permissions.map((permission) => (
                      <label key={permission} className="flex items-center gap-2 text-sm text-slate-700">
                        <input
                          type="checkbox"
                          checked={createFormik.values.permissions.includes(permission)}
                          onChange={() => toggleCreatePermission(permission)}
                          className="h-4 w-4 accent-indigo-600"
                        />
                        {permissionLabel(permission)}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          <div className="flex justify-end gap-3">
            <button type="button" disabled={adminSaving} onClick={closeCreateDialog} className="px-5 py-3 border border-slate-200 text-slate-700 font-bold rounded-xl hover:bg-slate-50">
              Cancel
            </button>
            {createStep === 'existing' && (
              <button
                type="button"
                disabled={adminSaving}
                onClick={() => setCreateStep('access')}
                className="px-5 py-3 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700"
              >
                Yes, Continue
              </button>
            )}
            {createStep !== 'existing' && (
              <button type="submit" disabled={adminSaving} className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 disabled:opacity-50">
                <CheckCircle2 size={18} />
                {adminSaving ? 'Saving...' : createStep === 'access' ? (existingAdmin ? 'Update Admin Access' : 'Create Admin') : 'Next'}
              </button>
            )}
          </div>
        </form>
      </Dialog>

      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 text-xs uppercase tracking-wider">
                <th className="p-4 font-bold">User</th>
                <th className="p-4 font-bold">Contact</th>
                <th className="p-4 font-bold">Role</th>
                <th className="p-4 font-bold">Permissions</th>
                <th className="p-4 font-bold">Status</th>
                <th className="p-4 font-bold text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {visibleAdminUsers.length === 0 ? (
                <tr>
                  <td colSpan="6" className="p-10 text-center text-slate-500">No admin users found.</td>
                </tr>
              ) : visibleAdminUsers.map((admin) => {
                const adminId = admin.id || admin._id;
                return (
                  <tr key={adminId} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
                          <Users size={18} />
                        </div>
                        <div>
                          <p className="font-bold text-slate-800">{admin.name || 'Admin User'}</p>
                          <p className="text-xs text-slate-500">ID: {adminId}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <p className="text-sm text-slate-700">{admin.email || '-'}</p>
                    </td>
                    <td className="p-4">
                      <span className="px-2.5 py-1 bg-slate-100 text-slate-700 rounded-lg text-xs font-bold uppercase tracking-wide">
                        {String(admin.role || '').replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="p-4 text-sm font-bold text-slate-700">{(admin.permissions || []).length}</td>
                    <td className="p-4">
                      <span className={`px-2.5 py-1 rounded-lg text-xs font-bold ${admin.isActive === false ? 'bg-rose-50 text-rose-700' : 'bg-emerald-50 text-emerald-700'}`}>
                        {admin.isActive === false ? 'Inactive' : 'Active'}
                      </span>
                    </td>
                    <td className="p-4 text-right">
                      <button
                        type="button"
                        onClick={() => navigate(`${routeBase}/user-management/${encodeURIComponent(adminId)}`)}
                        className="inline-flex items-center justify-center gap-2 px-3 py-2 text-xs font-bold text-indigo-600 border border-indigo-200 rounded-xl hover:bg-indigo-50"
                      >
                        <Pencil size={15} />
                        Manage
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
